'use client';

import { useThemeConfig } from '@snack-uikit/utils';
import dynamic from 'next/dynamic';
import { createContext, PropsWithChildren, useCallback, useEffect } from 'react';

import { KEY_CODES } from '@/constant/common';
import { useKeyDown } from '@/hooks/useKeyDown';
import { Theme, ThemeContextProps } from '@/types/theme';
import { equals } from '@/utils/common';

import { themeMap } from './constants';
import { getInitialTheme, updateTheme } from './utils';

const SnackIconsSpriteWithNoSSR = dynamic(async () => (await import('@/utils/snack')).SnackIconsSprite, { ssr: false });

export const ThemeContext = createContext<ThemeContextProps>({
  theme: Theme.Light,
  changeTheme() {},
});

export function ThemeContextProvider({ children }: PropsWithChildren) {
  const { theme, themeClassName, changeTheme } = useThemeConfig<Theme>({
    themeMap,
    defaultTheme: getInitialTheme().theme,
  });

  useEffect(() => {
    document.body.className = themeClassName;
  }, [themeClassName]);

  const handleUpdateTheme = useCallback(() => {
    const newTheme = equals(theme, Theme.Dark) ? Theme.Light : Theme.Dark;

    changeTheme(newTheme);
    updateTheme(newTheme);
  }, [changeTheme, theme]);

  useKeyDown(KEY_CODES.switchTheme, handleUpdateTheme, [handleUpdateTheme]);

  return (
    <ThemeContext.Provider value={{ theme, changeTheme }}>
      <SnackIconsSpriteWithNoSSR />
      {children}
    </ThemeContext.Provider>
  );
}
